import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';
import {
  GAME_GENRE_OPTIONS,
  GAME_MAX_GENRES,
  gameGenreLabel,
  toggleGameGenre,
} from '@/lib/game-genres';

/** "This arche is a game", with the genres it is listed under on the Games page. */
export interface GameListingValue {
  isGame: boolean;
  genres: string[];
}

interface GameListingFieldsProps {
  value: GameListingValue;
  onChange: (next: GameListingValue) => void;
  /** Distinguishes the checkbox's id when two forms are on one page. */
  idPrefix?: string;
  disabled?: boolean;
  /** How many genres this arche's game may name: 4, or what an admin allowed it. */
  maxGenres?: number;
}

/**
 * The game checkbox and genre chips, shared by the Arche Builder's Configure
 * panel and Fias Forge's publish options so both ask the same question the
 * same way. Unticking keeps nothing: a non-game names no genres.
 */
export function GameListingFields({
  value,
  onChange,
  idPrefix = 'arche',
  disabled = false,
  maxGenres = GAME_MAX_GENRES,
}: GameListingFieldsProps) {
  const checkboxId = `${idPrefix}-is-game`;
  const atLimit = value.genres.length >= maxGenres;
  const needsGenre = value.isGame && value.genres.length === 0;

  return (
    <div className="space-y-3">
      <div className="flex items-center space-x-2">
        <Checkbox
          id={checkboxId}
          checked={value.isGame}
          disabled={disabled}
          onCheckedChange={(checked) =>
            onChange({ isGame: checked === true, genres: checked === true ? value.genres : [] })
          }
        />
        <Label htmlFor={checkboxId} className="text-sm font-normal">
          This arche is a game (show it on the Games page)
        </Label>
      </div>

      {value.isGame && (
        <div className="space-y-2 pl-6">
          <p id={`${checkboxId}-genres-hint`} className="text-xs text-muted-foreground">
            Choose up to {maxGenres} genres.
          </p>
          <div
            role="group"
            aria-label="Game genres"
            aria-describedby={`${checkboxId}-genres-hint`}
            className="flex flex-wrap gap-2"
          >
            {GAME_GENRE_OPTIONS.map((genre) => {
              const selected = value.genres.includes(genre.id);
              const unavailable = disabled || (!selected && atLimit);
              return (
                <button
                  key={genre.id}
                  type="button"
                  aria-pressed={selected}
                  disabled={unavailable}
                  onClick={() =>
                    onChange({
                      isGame: true,
                      genres: toggleGameGenre(value.genres, genre.id, maxGenres),
                    })
                  }
                  className={cn(
                    'rounded-full border px-3 py-1 text-xs transition-colors',
                    selected
                      ? 'border-primary bg-primary text-primary-foreground'
                      : 'border-border bg-background hover:bg-muted',
                    unavailable && !selected && 'cursor-not-allowed opacity-50',
                  )}
                >
                  {genre.label}
                  {genre.parent && (
                    // A sub-genre's games are listed under its parent too.
                    <span className="opacity-70"> · in {gameGenreLabel(genre.parent)}</span>
                  )}
                </button>
              );
            })}
          </div>
          {needsGenre && (
            <p role="alert" className="text-xs text-destructive">
              Pick at least one genre so the game can be listed.
            </p>
          )}
        </div>
      )}
    </div>
  );
}
